/**
 * =============================================================================
 * NavDebugPanel.tsx
 * =============================================================================
 *
 * Pannello di debug fluttuante (bottom-right) che mostra in tempo reale gli
 * eventi di navigazione critici: redirect verso /auth, salvataggi e
 * ripristini di scroll/route, popstate, visibilitychange.
 *
 * ATTIVAZIONE:
 *  - Aggiungi `?debug=nav` ad un URL qualsiasi dell'app, oppure
 *  - Imposta in console: localStorage.setItem("lni:nav-debug", "1")
 *
 * In assenza del flag il componente non rende nulla e non ha overhead.
 * =============================================================================
 */

import { useEffect, useState } from "react";
import {
  clearNavDebugEvents,
  disableNavDebug,
  isNavDebugEnabled,
  type NavDebugEvent,
  subscribeNavDebug,
} from "@/lib/navDebugLogger";

/** Colori per livello (Tailwind classes via semantic tokens). */
const LEVEL_COLOR: Record<NavDebugEvent["level"], string> = {
  info: "text-foreground",
  warn: "text-yellow-500",
  error: "text-destructive",
};

export function NavDebugPanel() {
  // Stato locale degli eventi (sincronizzato col buffer del logger).
  const [events, setEvents] = useState<NavDebugEvent[]>([]);
  // Visibile/collassato.
  const [open, setOpen] = useState(true);
  // Flag attivo: ricomputato al mount, poi reattivo via storage event.
  const [enabled, setEnabled] = useState<boolean>(() => isNavDebugEnabled());

  useEffect(() => {
    if (!enabled) return;
    // Sottoscrizione al buffer eventi del logger.
    const unsub = subscribeNavDebug(setEvents);
    return () => unsub();
  }, [enabled]);

  // Ricontrolla il flag in caso di cambio URL (es. arrivo su ?debug=nav).
  useEffect(() => {
    const handle = () => setEnabled(isNavDebugEnabled());
    window.addEventListener("popstate", handle);
    return () => window.removeEventListener("popstate", handle);
  }, []);

  if (!enabled) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] w-[380px] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-card/95 shadow-lg backdrop-blur"
      // Stile inline volutamente assente: usiamo solo token semantici.
      role="complementary"
      aria-label="Pannello debug navigazione"
    >
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <strong className="text-xs font-semibold text-foreground">
          Nav Debug · {events.length} eventi
        </strong>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setOpen((value) => !value)}
            className="rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted"
          >
            {open ? "—" : "+"}
          </button>
          <button
            onClick={() => clearNavDebugEvents()}
            className="rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted"
          >
            Pulisci
          </button>
          <button
            onClick={() => {
              disableNavDebug();
              setEnabled(false);
            }}
            className="rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted"
          >
            Chiudi
          </button>
        </div>
      </div>

      {open && (
        <ol className="max-h-[40vh] overflow-y-auto px-3 py-2 text-[11px] font-mono leading-relaxed">
          {events.length === 0 && (
            <li className="text-muted-foreground">
              Nessun evento registrato. Cambia scheda o naviga per generare log.
            </li>
          )}
          {events
            .slice()
            .reverse()
            .map((event, index) => {
              const time = new Date(event.ts).toLocaleTimeString();
              return (
                <li key={`${event.ts}-${index}`} className={LEVEL_COLOR[event.level]}>
                  <span className="text-muted-foreground">{time}</span>{" "}
                  <span className="font-semibold">[{event.source}]</span>{" "}
                  {event.message}
                  {event.data && (
                    <pre className="ml-4 whitespace-pre-wrap text-muted-foreground">
                      {JSON.stringify(event.data)}
                    </pre>
                  )}
                </li>
              );
            })}
        </ol>
      )}
    </div>
  );
}